import React, { useState } from 'react'
import { Box, Paper, Typography, Grid, CircularProgress, makeStyles } from '@material-ui/core'
import { useHistory, generatePath, Link } from 'react-router-dom'
import { useQuery } from 'react-query'
import SearchField from 'UI/SearchField'
import Button from 'UI/Button/ButtonWithPreloader'
import KnowledgeBaseNavigation from 'components/KnowledgeBaseNavigation'
import KnowledgeBaseSearchResults from 'components/KnowledgeBaseSearchResults'
import KnowledgeBaseSearchEmpty from 'components/KnowledgeBaseSearchEmpty'
import useDebouncedValue from 'hooks/Custom/useDebouncedValue'
import { usePermission } from 'hooks/usePermission'
import { KB_MANAGE_PERM } from 'constants/permissions'
import { KB_NEW_URL, KB_VIEW_URL } from 'constants/routes'
import { getArticles, getSections, searchArticles } from 'services/knowledgeBase'

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: 'calc(100vh - 130px)',
  },
  card: {
    display: 'block',
    height: '100%',
    padding: '1.5rem',
    borderRadius: '8px',
    backgroundColor: theme.palette.info.light,
    color: theme.palette.text.secondary,
    textDecoration: 'none',
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor: theme.palette.info.main,
    },
  },
}))

const KnowledgeBaseMain = () => {
  const s = useStyles()
  const history = useHistory()
  const { hasPermission } = usePermission()
  const canManage = hasPermission(KB_MANAGE_PERM)

  const [search, setSearch] = useState('')
  const debouncedSearch = useDebouncedValue(search, 400)

  const { data: sections } = useQuery('kb-sections', getSections)

  const { data: articles } = useQuery('kb-articles', () => getArticles({ PageSize: 0 }))

  const { data: searchResult, isLoading: isSearching } = useQuery(
    ['kb-articles', 'search', debouncedSearch],
    () => searchArticles(debouncedSearch),
    { enabled: !!debouncedSearch }
  )

  const allSections = sections ? sections.data : []
  const allArticles = articles ? articles.data : []
  const results = searchResult ? searchResult.data : []
  const pinnedArticles = allArticles.filter((article) => article.isPinned)
  const isLoadingResults = !!debouncedSearch && isSearching

  return (
    <Box component={Paper} display="flex" alignItems="stretch" mb="2rem" className={s.root}>
      <KnowledgeBaseNavigation sections={allSections} articles={allArticles} />

      <Box flex={1} p="3rem" minWidth={0}>
        {canManage && (
          <Box display="flex" justifyContent="flex-end">
            <Button color="primary" variant="contained" onClick={() => history.push(KB_NEW_URL)}>
              Add Page
            </Button>
          </Box>
        )}

        <Box display="flex" flexDirection="column" alignItems="center">
          <Typography variant="h1" color="textSecondary">
            Knowledge Base
          </Typography>

          <Box mt="2rem" width="100%" maxWidth="36rem">
            <SearchField placeholder="Search" handleSearch={setSearch} onChange={(e) => setSearch(e.target.value)} />
          </Box>
        </Box>

        <Box mt="3rem">
          {isLoadingResults && (
            <Box display="flex" justifyContent="center" p="2rem">
              <CircularProgress color="primary" />
            </Box>
          )}

          {!isLoadingResults && debouncedSearch && results.length > 0 && (
            <KnowledgeBaseSearchResults results={results} search={debouncedSearch} />
          )}

          {!isLoadingResults && debouncedSearch && results.length === 0 && (
            <KnowledgeBaseSearchEmpty sections={allSections} articles={allArticles} />
          )}

          {!debouncedSearch && (
            <Grid container spacing={4}>
              {pinnedArticles.map((article) => (
                <Grid item xs={12} sm={6} md={4} key={article.id}>
                  <Typography
                    variant="body1"
                    component={Link}
                    to={generatePath(KB_VIEW_URL, { id: article.id })}
                    className={s.card}
                  >
                    {article.title}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          )}

          {!debouncedSearch && pinnedArticles.length === 0 && (
            <Box display="flex" justifyContent="center" p="2rem">
              <Typography variant="body1" color="textPrimary">
                No pinned pages yet
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default KnowledgeBaseMain
